import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildSelectedMetricData,
  getTotalUsers,
} from '../../utils/reportAnalysisUtils';
import ReportVariantGrid from './ReportVariantGrid';
import ReportRevenuePanel from './ReportRevenuePanel';
import { BRAND } from '../../theme/brandTheme';

const resultColors = {
  etkisiz: '#F1C40F',
  kazandı: BRAND.green,
  kaybetti: BRAND.redLink,
};

/**
 * Shared report analysis UI: metrics sidebar, variant grid, business impact
 */
const ReportAnalysisContent = ({
  originalData,
  metricIndex = 0,
  onMetricChange,
  reportId,
  onReportUpdated,
}) => {
  if (!originalData?.analysis?.length) {
    return null;
  }

  const metrics = originalData.analysis.map((analysis) => analysis.name);
  const selectedData = buildSelectedMetricData(originalData, metricIndex);

  if (!selectedData) return null;

  const resultKey = (selectedData.resultStatus || '').toLowerCase();

  return (
    <Box className="report-content-wrapper" sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>
      <Paper
        className="metrics-sidebar"
        elevation={0}
        sx={{
          width: { xs: '100%', md: '22%' },
          flexShrink: 0,
          border: `1px solid ${BRAND.borderLight}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Typography
          className="metrics-title"
          variant="subtitle2"
          sx={{ fontWeight: 600, px: 2, py: 1.5, bgcolor: '#f1f1f1', borderBottom: `1px solid ${BRAND.borderLight}` }}
        >
          Metrics
        </Typography>
        <List disablePadding className="metrics-list">
          {metrics.map((metricName, index) => (
            <ListItemButton
              key={index}
              selected={index === metricIndex}
              className={`metric-item ${index === metricIndex ? 'active' : ''}`}
              onClick={() => onMetricChange?.(index)}
              sx={{
                borderBottom: `1px solid ${BRAND.borderLight}`,
                '&.Mui-selected': {
                  bgcolor: '#e0e6ff',
                  borderLeft: `4px solid ${BRAND.purpleLight}`,
                  color: BRAND.purple,
                  fontWeight: 600,
                },
              }}
            >
              <ListItemText primary={metricName} primaryTypographyProps={{ fontSize: 14 }} />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      <Box className="report-main-content" sx={{ flex: 1, minWidth: 0 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          className="test-info"
          sx={{ mb: 2.5, border: `1px solid ${BRAND.borderLight}`, borderRadius: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f1f1f1' }}>
                <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Test Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Users</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{selectedData.formattedStartDate}</TableCell>
                <TableCell>{selectedData.formattedEndDate || 'In Progress'}</TableCell>
                <TableCell>{selectedData.testDuration} gün</TableCell>
                <TableCell>{getTotalUsers(selectedData.analysis)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <ReportVariantGrid
          originalData={originalData}
          analysis={selectedData.analysis}
          sessionTab={selectedData.sessionTab}
          conversionTab={selectedData.conversionTab}
        />

        <Paper
          elevation={0}
          className="test-conclusion"
          sx={{
            mt: 2.5,
            display: 'flex',
            border: `1px solid ${BRAND.borderLight}`,
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0,0,0,0.08)',
          }}
        >
          <Box
            className="conclusion-header"
            sx={{
              flex: 1.5,
              bgcolor: '#f1f1f1',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              borderRight: `1px solid ${BRAND.borderLight}`,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Business Impact:
            </Typography>
          </Box>
          <Box className="conclusion-content" sx={{ flex: 4, borderRight: `1px solid ${BRAND.borderLight}`, bgcolor: '#fff' }}>
            <TextField
              className="conclusion-input"
              multiline
              fullWidth
              defaultValue={selectedData.bussinessImpact}
              InputProps={{ readOnly: true }}
              variant="standard"
              sx={{ '& .MuiInput-root': { px: 2, py: 1.5, fontSize: 14 } }}
            />
          </Box>
          <Box className="conclusion-footer" sx={{ flex: 1.5, bgcolor: '#fff' }}>
            <Box
              className={`conclusion-result ${resultKey}`}
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Typography
                className="conclusion-result-title"
                variant="body2"
                fontWeight={600}
                sx={{
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f1f1f1',
                  borderBottom: `1px solid ${BRAND.borderLight}`,
                }}
              >
                Sonuç
              </Typography>
              <Typography
                className="conclusion-result-desc"
                variant="body2"
                fontWeight={600}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 94,
                  color: resultColors[resultKey] || 'text.primary',
                }}
              >
                {selectedData.resultStatus}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {reportId && (
          <Box sx={{ mt: 2 }}>
            <ReportRevenuePanel
              reportId={reportId}
              report={originalData}
              onUpdated={onReportUpdated}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportAnalysisContent;
